import type React from "react";
import { useState, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../Icon";
import {
  FaCheck,
  FaTimes,
  FaSave,
  FaTrashAlt,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import type { MemberInfo } from "@ctypes/dto/members";
import type { WalkerInfo } from "@ctypes/dto/walkers";

interface WalkerListItemProps {
  walker: WalkerInfo;
  isLeader: boolean;
  nickname: string;
  memberList: MemberInfo[];
  walkerListTypes: string[];
  onSave: (walker: WalkerInfo) => void;
  onRemove: (walkerID: number) => void;
}

const WalkerListItem: React.FC<WalkerListItemProps> = ({
  walker,
  isLeader,
  nickname,
  memberList,
  walkerListTypes,
  onSave,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [walkerState, setWalkerState] = useState(walker);
  const canEdit =
    isLeader || walker.ownerUser === nickname || walker.lastuser === nickname;

  const handleWalkerUpdate = useCallback(
    (field: string, value: string | boolean) => {
      setWalkerState((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const walkerInfo = isOpen ? (
    <tr>
      <td colSpan={6} className="px-6 py-4 bg-gray-900">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="walkerID"
                    className="block text-sm font-medium text-gray-300"
                  >
                    {t("walkers.walkerId")}
                  </label>
                  <input
                    id="walkerID"
                    type="text"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={walker.walkerid}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="lastUser"
                    className="block text-sm font-medium text-gray-300"
                  >
                    {t("walkers.lastUser")}
                  </label>
                  <input
                    id="lastUser"
                    type="text"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={walker.lastuser}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="lastUse"
                    className="block text-sm font-medium text-gray-300"
                  >
                    {t("walkers.lastUse")}
                  </label>
                  <input
                    id="lastUse"
                    type="text"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={walker.datelastuse}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="inputOwner"
                    className="block text-sm font-medium text-gray-300"
                  >
                    {t("common.owner")}
                  </label>
                  <select
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="inputOwner"
                    value={walkerState.ownerUser ?? ""}
                    onChange={(e) =>
                      handleWalkerUpdate("ownerUser", e.target.value)
                    }
                    disabled={!canEdit}
                  >
                    <option value="clan">{t("common.clan")}</option>
                    {memberList?.map((member) => (
                      <option key={member.discordid} value={member.nickname}>
                        {member.nickname ?? member.discordtag}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="inputUse"
                    className="block text-sm font-medium text-gray-300"
                  >
                    {t("common.use")}
                  </label>
                  <select
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="inputUse"
                    value={walkerState.use ?? "None"}
                    onChange={(e) =>
                      handleWalkerUpdate("walker_use", e.target.value)
                    }
                    disabled={!canEdit}
                  >
                    {["None", "Personal", "PVP", "RAM", "Farming"].map(
                      (use) => (
                        <option key={use} value={use}>
                          {t(use)}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="inputType"
                    className="block text-sm font-medium text-gray-300"
                  >
                    {t("common.type")}
                  </label>
                  <select
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="inputType"
                    value={walkerState.type ?? ""}
                    onChange={(e) => handleWalkerUpdate("type", e.target.value)}
                    disabled={!canEdit}
                  >
                    <option value="" />
                    {walkerListTypes.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-300"
                  >
                    {t("common.description")}
                  </label>
                  <textarea
                    id="description"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={walkerState.description ?? ""}
                    onChange={(e) =>
                      handleWalkerUpdate("description", e.target.value)
                    }
                    maxLength={200}
                    disabled={!canEdit}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="flex flex-col space-y-4 items-center mt-3">
              <div className="flex space-x-2">
                <button
                  type="button"
                  className={`p-2 rounded-l-lg focus:outline-none ${
                    walkerState.isReady
                      ? "bg-green-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-green-600 hover:text-white"
                  }`}
                  onClick={() => handleWalkerUpdate("isReady", true)}
                >
                  <FaCheck />
                </button>
                <span className="p-2 bg-gray-700 text-gray-300">
                  {t("common.isReady")}
                </span>
                <button
                  type="button"
                  className={`p-2 rounded-r-lg focus:outline-none ${
                    walkerState.isReady
                      ? "bg-gray-700 text-gray-300 hover:bg-red-600 hover:text-white"
                      : "bg-red-600 text-white"
                  }`}
                  onClick={() => handleWalkerUpdate("isReady", false)}
                >
                  <FaTimes />
                </button>
              </div>

              <button
                type="button"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 w-full max-w-xs"
                onClick={() => {
                  onSave(walkerState);
                  setIsOpen(false);
                }}
              >
                <FaSave className="mr-2 inline" /> {t("common.save")}
              </button>

              {canEdit && (
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 w-full max-w-xs"
                  onClick={() => onRemove(walker.walkerid)}
                >
                  <FaTrashAlt className="mr-2 inline" /> {t("common.delete")}
                </button>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  ) : null;

  if (!walker.walkerid) {
    return "";
  }

  return (
    <>
      <tr className="hover:bg-gray-700">
        <td className="px-6 py-4 text-center whitespace-nowrap">
          {walker.type && (
            <Icon
              key={`${walker.type} Walker`}
              name={`${walker.type} Walker`}
              width={30}
            />
          )}
        </td>
        <td className="px-6 py-4 text-center whitespace-nowrap text-gray-300">
          {walker.name}
        </td>
        <td className="hidden sm:table-cell px-6 py-4 text-center whitespace-nowrap text-gray-300">
          {walker.use ?? t("common.notDefined1")}
        </td>
        <td className="hidden sm:table-cell px-6 py-4 text-center text-gray-300">
          {walker.description}
        </td>
        <td className="px-6 py-4 text-center whitespace-nowrap">
          <FaCheck
            className={`${
              walker.isReady ? "check text-green-500" : "times text-red-500"
            }`}
          />
        </td>
        <td className="px-6 py-4 text-center whitespace-nowrap">
          <button
            className="text-blue-400 hover:text-blue-300 focus:outline-none"
            onClick={() => setIsOpen(!isOpen)}
            type="button"
            aria-label={
              isOpen ? t("common.hideDetails") : t("common.showDetails")
            }
          >
            {isOpen ? <FaEyeSlash /> : <FaEye />}
          </button>
        </td>
      </tr>
      {walkerInfo}
    </>
  );
};

export default memo(WalkerListItem);
